import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, MenuItem, TextField, Typography, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';

// "Árazás" szekció a /reszletek oldalon: kategória kiválasztása után 4 oszlopos összehasonlító
// táblázatban mutatja a jutalékos (ingyenes) modellt és a három kiemelési szintet.
// Adatforrás: GET /api/featured/pricing (Category.commissionPercent + CategoryTierLimit).
// ?kategoria=<slug> előre kiválasztja a kategóriát.

const formatFt = (n) => `${Math.round(n).toLocaleString('hu-HU')} Ft`;

// Áthúzott "eredeti" ár: +12,5%, 500 Ft-ra kerekítve, de legalább +500 Ft
export const listPriceFor = (price) => Math.max(price + 500, Math.round((price * 1.125) / 500) * 500);

export const PLANS = [
  { key: 'FREE', name: 'Ingyenes', tagline: 'Csak sikeres foglalás után fizetsz' },
  { key: 'TIER3', name: 'Előfizetés', tagline: 'Előrébb az ingyeneseknél' },
  { key: 'TIER2', name: 'Kiemelés', tagline: 'Rögtön a prémiumok után' },
  { key: 'TOP', name: 'Kiemelés prémium', tagline: 'Kategóriánként csak 3 hely', highlight: true },
];

// Előny-sorok: érték csomagonként — true/false (pipa / X) vagy szöveg. A "commission" sor
// értéke a kategóriától függ, ezért azt a render tölti ki.
export const FEATURES = [
  { label: 'Saját profil a piactéren, korlátlan ajánlatkérés', values: { FREE: true, TIER3: true, TIER2: true, TOP: true } },
  { label: 'Egyedi ajánlatkérő kérdőív, email értesítések', values: { FREE: true, TIER3: true, TIER2: true, TOP: true } },
  { key: 'commission', label: 'Jutalék sikeres foglalás után' },
  { label: 'Részletes statisztikák a profilod látogatásairól', values: { FREE: false, TIER3: true, TIER2: true, TOP: true } },
  {
    label: 'Helyezés a keresési találatokban',
    values: { FREE: 'Alap sorrend', TIER3: 'Az ingyenesek előtt', TIER2: 'Rögtön a prémiumok után', TOP: 'Mindig a lista elején' },
  },
  { label: 'Kiemelt hely a főoldalon', values: { FREE: false, TIER3: false, TIER2: false, TOP: true } },
  { label: 'A Wedigo reklámkampányaiban a te szolgáltatásoddal hirdetünk', values: { FREE: false, TIER3: false, TIER2: false, TOP: true } },
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const colors = { dark: '#1b331f', green: '#2a5d32', muted: '#5b6e5d', gold: '#edbe5f', goldDark: '#c9932a' };
const highlightBg = '#fffaf0';
const labelColSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: '#fff',
  width: { xs: 150, sm: '24%' },
  borderRight: '1px solid rgba(42,93,50,0.1)',
};

// A havidíj cella fix "rácsa": áthúzott ár / ár / egység — minden oszlopban azonos
// magasságú sorokkal, hogy az árak egy vonalban legyenek.
const PRICE_LINE_HEIGHTS = { strike: 18, price: 30, unit: 18 };

const renderValue = (value) => {
  if (value === true) return <CheckIcon fontSize="small" sx={{ color: colors.green }} />;
  if (value === false) return <CloseIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.25)' }} />;
  return <Typography variant="body2" sx={{ color: colors.dark }}>{value}</Typography>;
};

const PricingSection = () => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  // Telefonon nem fér ki a 4 oszlopos táblázat: csomagválasztó + egy csomag kártyája
  const isPhone = useMediaQuery('(max-width:599px)');
  const [mobilePlan, setMobilePlan] = useState('TOP');

  useEffect(() => {
    apiService.get('/featured/pricing')
      .then((response) => {
        const list = response?.data || [];
        setCategories(list);
        const preselect = list.find(c => c.slug === searchParams.get('kategoria'));
        if (preselect) setCategoryId(preselect.id);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const category = useMemo(() => categories.find(c => c.id === categoryId), [categories, categoryId]);
  const tierOf = (key) => category?.tiers.find(t => t.tier === key);

  const commissionValue = (planKey) => {
    if (planKey !== 'FREE') return 'Nincs jutalék';
    if (!category) return '—';
    return category.commissionPercent !== null ? `${category.commissionPercent}%` : '—';
  };

  const priceLines = (strike, price, unit, priceColor = colors.dark) => (
    <Box>
      <Box sx={{ height: PRICE_LINE_HEIGHTS.strike, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {strike && (
          <Typography variant="caption" sx={{ color: colors.muted, textDecoration: 'line-through' }}>{strike}</Typography>
        )}
      </Box>
      <Box sx={{ height: PRICE_LINE_HEIGHTS.price, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, color: priceColor, whiteSpace: 'nowrap' }}>{price}</Typography>
      </Box>
      <Box sx={{ height: PRICE_LINE_HEIGHTS.unit, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ color: colors.muted }}>{unit}</Typography>
      </Box>
    </Box>
  );

  const renderPrice = (plan) => {
    if (plan.key === 'FREE') return priceLines(null, '0 Ft', 'havidíj nélkül');
    if (!category) return priceLines(null, '—', 'válassz kategóriát', colors.muted);
    const tier = tierOf(plan.key);
    if (!tier) return priceLines(null, '—', 'nem elérhető', colors.muted);
    return priceLines(formatFt(listPriceFor(tier.monthlyPrice)), formatFt(tier.monthlyPrice), '/ hó');
  };

  const renderSlots = (plan) => {
    if (plan.key !== 'TOP') return renderValue('Korlátlan');
    if (!category) return renderValue('3 / kategória');
    const tier = tierOf('TOP');
    if (!tier) return renderValue('—');
    const free = tier.availableSlots > 0;
    return (
      <Chip
        size="small"
        label={free ? `${tier.availableSlots} / ${tier.maxSlots} szabad` : 'Betelt'}
        sx={{ fontWeight: 700, bgcolor: free ? 'rgba(42,93,50,0.1)' : 'rgba(0,0,0,0.06)', color: free ? colors.green : colors.muted }}
      />
    );
  };

  const renderAnnual = (plan) => {
    if (plan.key === 'FREE') return renderValue('—');
    const tier = tierOf(plan.key);
    if (!tier) return renderValue('—');
    return (
      <Box>
        <Typography variant="body2" sx={{ color: colors.dark, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatFt(tier.annualPrice)}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.muted, display: 'block', lineHeight: 1.3 }}>/ év</Typography>
        <Typography variant="caption" sx={{ color: colors.green, display: 'block', lineHeight: 1.3 }}>2 hónap ingyen</Typography>
      </Box>
    );
  };

  const cellSx = (plan) => ({
    textAlign: 'center',
    bgcolor: plan.highlight ? highlightBg : undefined,
    borderLeft: plan.highlight ? `2px solid ${colors.gold}` : undefined,
    borderRight: plan.highlight ? `2px solid ${colors.gold}` : undefined,
    px: { xs: 1, sm: 1.5 },
    width: { xs: 120, sm: '19%' },
  });

  // Telefonos nézet: a csomag neve + a táblázat sorai egymás alatt, "címke — érték" párokban
  const renderMobile = () => {
    const plan = PLANS.find(p => p.key === mobilePlan);
    const rows = [
      { label: 'Éves fizetéssel', node: renderAnnual(plan) },
      { label: 'Elérhető helyek', node: renderSlots(plan) },
      ...FEATURES.map(f => ({
        label: f.label,
        node: f.key === 'commission'
          ? <Typography variant="body2" fontWeight={700} sx={{ color: plan.key === 'FREE' ? colors.dark : colors.green }}>{commissionValue(plan.key)}</Typography>
          : renderValue(f.values[plan.key]),
      })),
    ];
    return (
      <Box>
        <Box
          role="tablist"
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5, p: 0.5, mb: 1.5, borderRadius: 2, bgcolor: 'rgba(42,93,50,0.08)' }}
        >
          {PLANS.map(p => {
            const active = p.key === mobilePlan;
            return (
              <Box
                key={p.key}
                component="button"
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMobilePlan(p.key)}
                sx={{
                  border: 0,
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  py: 1,
                  px: 0.5,
                  font: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: active ? (p.highlight ? colors.goldDark : colors.dark) : colors.muted,
                  bgcolor: active ? '#fff' : 'transparent',
                  boxShadow: active ? '0 1px 4px rgba(27,51,31,0.15)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {p.key === 'TOP' ? 'Prémium' : p.name}
              </Box>
            );
          })}
        </Box>

        <Paper
          key={plan.key}
          variant="outlined"
          sx={{
            p: 2,
            animation: `${fadeIn} 0.3s ease both`,
            borderColor: plan.highlight ? colors.gold : 'rgba(42,93,50,0.15)',
            borderWidth: plan.highlight ? 2 : 1,
            bgcolor: plan.highlight ? highlightBg : 'rgba(255,255,255,0.95)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 1.5 }}>
            <Typography fontWeight={800} sx={{ fontSize: 20, color: plan.highlight ? colors.goldDark : colors.dark }}>{plan.name}</Typography>
            <Typography variant="body2" sx={{ color: colors.muted, mb: 1 }}>{plan.tagline}</Typography>
            {renderPrice(plan)}
          </Box>
          {rows.map(row => (
            <Box
              key={row.label}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.25, borderTop: '1px solid rgba(42,93,50,0.1)' }}
            >
              <Typography variant="body2" sx={{ color: colors.dark, flex: 1 }}>{row.label}</Typography>
              <Box sx={{ flexShrink: 0, maxWidth: '45%', textAlign: 'right' }}>{row.node}</Box>
            </Box>
          ))}
        </Paper>
      </Box>
    );
  };

  const labelCell = (text, strong) => (
    <TableCell sx={labelColSx}>
      <Typography variant="body2" fontWeight={strong ? 700 : 400} sx={{ color: colors.dark }}>{text}</Typography>
    </TableCell>
  );

  return (
    <Box id="arazas" sx={{ mb: { xs: 5, sm: 7 } }}>
      <Typography variant="body2" align="center" sx={{ color: colors.muted, mb: 3, maxWidth: 520, mx: 'auto' }}>
        Válaszd ki a kategóriádat, és megmutatjuk, mennyibe kerül a jutalékos csomag és a kiemelési lehetőségek.
      </Typography>

      {loadError && <Alert severity="warning" sx={{ mb: 2 }}>Az árazás most nem tölthető be. Kérjük, próbáld újra később.</Alert>}

      <Box sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
        <TextField
          select
          fullWidth
          label="Kategória"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          disabled={loading || loadError}
          InputProps={loading ? { endAdornment: <CircularProgress size={18} sx={{ mr: 3 }} /> } : undefined}
          sx={{ bgcolor: '#fff' }}
        >
          {categories.map(c => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {isPhone ? renderMobile() : (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderColor: 'rgba(42,93,50,0.15)', bgcolor: 'rgba(255,255,255,0.9)' }}
      >
        <Table size="small" sx={{ minWidth: 640, tableLayout: 'fixed', '& td, & th': { borderColor: 'rgba(42,93,50,0.1)' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={labelColSx} />
              {PLANS.map(plan => (
                <TableCell
                  key={plan.key}
                  sx={{ ...cellSx(plan), borderTop: plan.highlight ? `2px solid ${colors.gold}` : undefined, verticalAlign: 'top', pt: 2 }}
                >
                  {/* Fix magasságú név + alcím, hogy a fejléc-sor minden oszlopban egyforma legyen */}
                  <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography fontWeight={800} sx={{ color: plan.highlight ? colors.goldDark : colors.dark, lineHeight: 1.2 }}>
                      {plan.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.muted, display: 'block', lineHeight: 1.3 }}>
                    {plan.tagline}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {labelCell('Havidíj', true)}
              {PLANS.map(plan => <TableCell key={plan.key} sx={{ ...cellSx(plan), py: 1.5 }}>{renderPrice(plan)}</TableCell>)}
            </TableRow>
            <TableRow>
              {labelCell('Éves fizetéssel')}
              {PLANS.map(plan => <TableCell key={plan.key} sx={cellSx(plan)}>{renderAnnual(plan)}</TableCell>)}
            </TableRow>
            <TableRow>
              {labelCell('Elérhető helyek kategóriánként')}
              {PLANS.map(plan => <TableCell key={plan.key} sx={cellSx(plan)}>{renderSlots(plan)}</TableCell>)}
            </TableRow>
            {FEATURES.map((feature, idx) => (
              <TableRow key={feature.label}>
                {labelCell(feature.label)}
                {PLANS.map(plan => (
                  <TableCell
                    key={plan.key}
                    sx={{
                      ...cellSx(plan),
                      borderBottom: plan.highlight && idx === FEATURES.length - 1 ? `2px solid ${colors.gold}` : undefined,
                    }}
                  >
                    {feature.key === 'commission'
                      ? <Typography variant="body2" fontWeight={700} sx={{ color: plan.key === 'FREE' ? colors.dark : colors.green }}>{commissionValue(plan.key)}</Typography>
                      : renderValue(feature.values[plan.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Typography variant="caption" align="center" display="block" sx={{ color: colors.muted, mt: 2 }}>
        Az árak bruttó összegek, és az első 500 csatlakozó szolgáltatóra vonatkoznak. Jutalékot csak az ingyenes
        csomagban számolunk fel — a kiemelési csomagok mellett a foglalások után nincs jutalék.
      </Typography>
    </Box>
  );
};

export default PricingSection;
